import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { TYPE_KEYS, TYPE_META, CONTACT } from "../lib/constants";
import AccommodationCard from "../components/AccommodationCard";
import useTitle from "../lib/useTitle";

// fix default leaflet marker
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const makeIcon = (color) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:${color};width:22px;height:22px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

export default function Accommodations() {
  const { t } = useI18n();
  useTitle(t.nav.stays);
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();
  const filter = params.get("type") || "all";

  useEffect(() => {
    api.get("/accommodations").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const filtered = filter === "all" ? items : items.filter((x) => x.type === filter);
  const mapped = filtered.filter((x) => x.latitude != null && x.longitude != null);

  const filters = [
    { k: "all", label: t.stays.filterAll },
    { k: "hostel", label: t.stays.filterHostel },
    { k: "guest_house", label: t.stays.filterGuest },
    { k: "apartment", label: t.stays.filterApartment },
  ];

  return (
    <main className="container-x py-16" data-testid="stays-page">
      <header className="max-w-3xl mb-12">
        <span className="eyebrow">{t.nav.stays}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">{t.stays.title}</h1>
        <p className="mt-5 text-lg text-wostel-ink/70 leading-relaxed">{t.stays.sub}</p>
      </header>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="stays-filters">
        {filters.map((f) => (
          <button key={f.k} onClick={() => setParams(f.k === "all" ? {} : { type: f.k })} data-testid={`filter-${f.k}`}
            className={`btn-pill text-sm border ${filter === f.k ? "bg-wostel-ink text-sand-50 border-wostel-ink" : "bg-transparent text-wostel-ink/70 border-sand-300 hover:border-wostel-ink"}`}>
            {f.k !== "all" && <img src={TYPE_META[f.k]?.logo} alt="" className="h-5 w-5" />}
            {f.label}
          </button>
        ))}
      </div>

      {/* MAP */}
      <div className="rounded-3xl overflow-hidden border border-sand-300/60 mb-12 h-[420px] relative" data-testid="stays-map">
        <MapContainer center={[CONTACT.mainCoords.lat, CONTACT.mainCoords.lng]} zoom={11} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {mapped.map((acc) => (
            <Marker key={acc.id} position={[acc.latitude, acc.longitude]} icon={makeIcon(TYPE_META[acc.type]?.color || "#1FB6D9")}>
              <Popup>
                <div className="font-serif text-base">{acc.name}</div>
                <div className="text-xs text-wostel-ink/60 mb-2">{acc.location_name}</div>
                {acc.hostelworld_url && <a href={acc.hostelworld_url} target="_blank" rel="noreferrer" className="text-wostel-cyan text-xs underline">Hostelworld</a>}
                {acc.whatsapp_url && <> · <a href={acc.whatsapp_url} target="_blank" rel="noreferrer" className="text-wostel-sage text-xs underline">WhatsApp</a></>}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {filtered.length === 0 ? (
        <div className="py-24 text-center text-wostel-ink/50" data-testid="stays-empty">{t.stays.empty}</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((acc) => <AccommodationCard key={acc.id} acc={acc} />)}
        </div>
      )}
    </main>
  );
}
